import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Menu, Moon, Search, Sun, Workflow, X, Home, Plus, PhoneCall, KanbanSquare,
  TrendingUp, UserPlus, LogOut, Check, Users, ChevronDown,
  BarChart3, HelpCircle, CalendarCheck,
} from "lucide-react";
import { MODULES, ZONES, getModule, modulesByZone, moduleHref } from "@/config/modules";
import { useAuth } from "@/state/auth";
import { useAgencySettings, parseEnabledModules } from "@/hooks/useAgencySettings";
import { applyBrandColor } from "@/lib/brand";
import { useTheme } from "@/components/layout/ThemeProvider";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { Jarvis } from "@/components/layout/Jarvis";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { QuickSync } from "@/components/layout/QuickSync";
import { HelpButton } from "@/components/help/HelpButton";
import { ProspectDrawer } from "@/components/features/ProspectDrawer";
import { AccountContextBanner } from "@/components/layout/AccountContextBanner";
import { useActiveAccount } from "@/state/accountContext";
import { usePerm, roleDisplayLabel } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/common/bits";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { AgentBubble } from "@/components/layout/AgentBubble";


function currentTitle(pathname: string): string {
  const id = pathname.replace(/^\//, "").split("?")[0];
  if (id === "" || id === "launcher") return "Home";
  if (id === "help" || id.startsWith("help/")) return "Help";
  return getModule(id)?.title ?? "Flowio";
}

const BOTTOM_NAV = [
  { to: "/today", label: "Today", icon: Home },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare },
  { to: "/live-call", label: "Call", icon: PhoneCall },
  { to: "/booking-board", label: "Board", icon: CalendarCheck },
  { to: "/sales-tracking", label: "Track", icon: TrendingUp },
];

export function AppShell() {
  const { theme, toggle } = useTheme();
  const { user, signOut, agencies, activeAgency, switchAgency, createAgency, canCreateAgency } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [newAgencyName, setNewAgencyName] = useState("");
  const [creating, setCreating] = useState(false);
  const { settings: agencySettings } = useAgencySettings();
  const { settings } = useAgencySettings();

  // Per-agency brand color — rebrands --primary and friends on load and on
  // agency switch, Field Suite style (theirs is per-deployment; ours is
  // per-tenant). Clears back to the stylesheet default when unset.
  useEffect(() => {
    applyBrandColor(agencySettings?.brand_color);
  }, [agencySettings?.brand_color]);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const title = currentTitle(pathname);
  const wsName = agencySettings?.display_name || activeAgency?.name || "Agency";
  const { activeAccountId } = useActiveAccount();

  // enabled_modules unset ⇒ show everything (preserves existing UX for SFP).
  // When set, filter the sidebar to only those IDs; the routes still resolve
  // via direct URL — the flag hides them from discovery only.
  const enabled = parseEnabledModules(settings?.enabled_modules);
  const { can } = usePerm();
  const isEnabled = (id: string) => {
    if (enabled !== null && !enabled.includes(id)) return false;
    const perm = getModule(id)?.perm;
    return !perm || can(perm);
  };

  // Reflect the active agency + current section in the tab title.
  useEffect(() => {
    document.title = `${title} · ${wsName} — Flowio`;
  }, [title, wsName]);

  const openPalette = () =>
    window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }));

  const submitCreateAgency = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newAgencyName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const w = await createAgency(name);
      toast.success(`Created ${w.name}`);
      setNewAgencyName("");
      setCreateOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't create agency");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      <CommandPalette />
      <Jarvis />
      <ProspectDrawer />

      {/* Sidebar (desktop) */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-transform md:static md:translate-x-0 [box-shadow:var(--shadow-sidebar)]",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-16 items-center justify-between gap-2 border-b border-sidebar-border px-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex flex-1 items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
                title="Switch agency"
              >
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary">
                  <Workflow className="size-5 text-primary-foreground" />
                </div>
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="text-base font-bold tracking-tight">Flowio</div>
                  <div className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">{wsName}</div>
                </div>
                <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-60">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Agencies</DropdownMenuLabel>
              {agencies.map((w) => (
                <DropdownMenuItem
                  key={w.id}
                  onClick={() => { if (w.id !== activeAgency?.id) switchAgency(w.id); }}
                >
                  <span className="flex-1 truncate">{w.name}</span>
                  <span className="text-[10px] uppercase text-muted-foreground">{roleDisplayLabel(w.role)}</span>
                  {w.id === activeAgency?.id && <Check className="ml-2 size-3.5 text-primary" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              {canCreateAgency && (
                <DropdownMenuItem
                  onSelect={(e) => {
                    // Let the dropdown close normally, then open the dialog on
                    // the next tick so Radix's focus trap doesn't fight ours.
                    e.preventDefault();
                    setTimeout(() => setCreateOpen(true), 0);
                  }}
                >
                  <Plus /> Create agency
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate("/settings?tab=team")}>
                <Users /> Manage team
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button className="text-muted-foreground md:hidden" aria-label="Close menu" onClick={(e) => { e.preventDefault(); setMobileOpen(false); }}>
            <X className="size-5" />
          </button>
        </div>

        {/* Field Suite nav: solid-primary active state, hover slides right,
            hairline-ruled section labels. "/" is the card-based Home. */}
        <nav className="flex-1 space-y-4 overflow-y-auto scroll-slim px-3 py-3">
          <NavLink to="/" end onClick={() => setMobileOpen(false)}
            className={({ isActive }) => cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-150",
              isActive
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:translate-x-1 hover:bg-muted hover:text-primary"
            )}>
            <Home className="size-4 shrink-0" /> Home
          </NavLink>
          {ZONES.map((z, zi) => (
            <div key={z.id} className="space-y-0.5">
              <p className={cn(
                "flex items-center justify-between px-3 pb-1",
                zi > 0 && "mt-1 border-t border-border pt-2.5"
              )}>
                <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">{z.id}</span>
                <span className="text-[10px] text-muted-foreground/60">{z.blurb}</span>
              </p>
              {modulesByZone(z.id).filter((m) => isEnabled(m.id)).map((m) => (
                <NavLink key={m.id} to={moduleHref(m, activeAccountId)} onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => cn(
                    "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-150",
                    isActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:translate-x-1 hover:bg-muted hover:text-primary"
                  )}>
                  <m.icon className="size-4 shrink-0" />
                  <span className="truncate">{m.title}</span>
                  {m.hero && <span className="ml-auto size-1.5 rounded-full bg-cta" title="Hero feature" />}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="space-y-2 border-t border-sidebar-border p-3">
          <NavLink to="/help" onClick={() => setMobileOpen(false)}
            className={({ isActive }) => cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-xs font-medium transition-colors",
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent/50 hover:text-primary"
            )}>
            <HelpCircle className="size-3.5" /> Help center
          </NavLink>
          <button onClick={openPalette} className="flex w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-accent/50">
            <Search className="size-3.5" /><span>Quick jump</span>
            <span className="ml-auto flex gap-0.5"><Kbd>⌘</Kbd><Kbd>K</Kbd></span>
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-2 border-b border-border bg-background/85 px-3 backdrop-blur-md sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button className="text-muted-foreground md:hidden" aria-label="Open menu" onClick={() => setMobileOpen(true)}>
              <Menu className="size-5" />
            </button>
            <h1 className="hidden text-base font-semibold sm:block">{title}</h1>
            <AccountContextBanner />
          </div>
          <div className="flex min-w-0 items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={openPalette} className="hidden gap-2 lg:flex">
              <Search className="size-3.5" /><span className="text-muted-foreground">Search</span><Kbd>⌘K</Kbd>
            </Button>
            <QuickSync />
            <NotificationBell />
            <HelpButton />
            <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold uppercase text-primary-foreground"
                  aria-label="Account"
                  title={user?.email}
                >
                  {(user?.name || user?.email || "?").trim().charAt(0)}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-56">
                <DropdownMenuLabel className="flex flex-col gap-0.5">
                  <span className="truncate font-semibold">{user?.name || "Signed in"}</span>
                  <span className="truncate text-xs font-normal text-muted-foreground">{user?.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={signOut}><LogOut /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden scroll-slim">
          <div className="mx-auto max-w-7xl px-4 pb-24 pt-6 sm:px-6 md:pb-10 animate-in-up">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom nav + FAB */}
      <nav className="fixed inset-x-0 bottom-0 z-30 flex items-stretch border-t border-border bg-background/95 backdrop-blur md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {BOTTOM_NAV.map((item) => (
          <NavLink key={item.to} to={item.to}
            className={({ isActive }) => cn("flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors", isActive ? "text-primary" : "text-muted-foreground")}>
            <item.icon className="size-5" />{item.label}
          </NavLink>
        ))}
      </nav>

      <Dialog open={createOpen} onOpenChange={(o) => { if (!creating) setCreateOpen(o); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create a new agency</DialogTitle>
            <DialogDescription>
              Each agency is fully isolated — its own data, team, and settings. You can switch between them anytime.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreateAgency} className="space-y-3">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Agency name</span>
              <Input
                autoFocus
                value={newAgencyName}
                onChange={(e) => setNewAgencyName(e.target.value)}
                placeholder="Acme Agency"
                className="mt-1"
              />
            </label>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
                Cancel
              </Button>
              <Button type="submit" disabled={!newAgencyName.trim() || creating}>
                {creating ? "Creating…" : "Create agency"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Team-only agent bubble, stacked above Quick add. Self-hides without agent perms. */}
      <AgentBubble moduleEnabled={enabled === null || enabled.includes("agents")} pageTitle={title} />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="fixed bottom-16 right-4 z-40 flex size-14 items-center justify-center rounded-full bg-cta text-cta-foreground shadow-lg transition-transform active:scale-90 md:bottom-6" style={{ marginBottom: "env(safe-area-inset-bottom)" }} aria-label="Quick add">
            <Plus className="size-6" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="end" className="mb-2">
          <DropdownMenuLabel>Quick capture</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => navigate("/pipeline?new=1")}><UserPlus /> New prospect</DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/audits")}>
            <BarChart3 /> Audit reports
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export { MODULES };
